/**
 * TabsList and TabsItem have two customizable areas:
 * `Views` and `Behaviours` all the TabsList/Item groups have the following combination order:
 *   1. LayoutViews -> 2. BehaviorViews -> 3. ContextBindings
 *
 * 1. The `LayoutViews` are added into the Views folder
 * 2. Behaviors are optional, to apply behavior one of the behavior enhancer HOCs can be used
 *   (`withDropdown`, `withScrollContainer`),
 * 3. Once the composition is complete List/Item group must be extended via `withContext` enhancer
 *
 * List/Item group can also be customized via Switch layout at layout or behavior stage
 * to join multiple behaviors into a single component controlled by a prop
 *
 *
 * The tiniest possible combination would be using a specific view straight out of the `./Views/*` subfolder
 * and applying withContext to it, as shown in the following example:
 *
 *   import { SegmentedTabsView, SegmentedTabsItemView } from './Views/SegmentedTabs';
 *   const [SegmentedTabsList, SegmentedTabsItem] = withContext([SegmentedTabsListView, SegmentedTabsItemView]);
 *
 */

import React from 'react';
import PropTypes from 'prop-types';

import withContext from './enhancers/withContext';
import withState from './enhancers/withState';
import { SimpleTabsListView, SimpleTabsItemView } from './views';
import { ScrollableTabsListView, ScrollableTabsItemView } from './ScrollableTabs';
import { DropdownTabsListView, DropdownTabsItemView } from './DropdownTabs';
import { TAB_OVERFLOW_SCROLL, TAB_OVERFLOW_DROPDOWN, TabOverflowTypes } from './constants';

/**
 * Simple Tab View Group Container, has additional overflow argument
 * that works as a switch for overflow type
 * These tabs are not production ready, they still require context to function
 */
function TabsListView(props) {
  switch (props.overflow) {
    case TAB_OVERFLOW_SCROLL:
      return <ScrollableTabsListView {...props} />;
    case TAB_OVERFLOW_DROPDOWN:
      return <DropdownTabsListView {...props} />;
    default:
      return <SimpleTabsListView {...props} />;
  }
}

TabsListView.propTypes = {
  overflow: PropTypes.oneOf(TabOverflowTypes),

  /** inherited props */
};

/**
 *  Tab View Group Item, grouped with TabsListView
 */
function TabsItemView({ overflow, ...props }) {
  switch (overflow) {
    case TAB_OVERFLOW_SCROLL:
      return <ScrollableTabsItemView {...props} />;
    case TAB_OVERFLOW_DROPDOWN:
      return <DropdownTabsItemView {...props} />;
    default:
      return <SimpleTabsItemView {...props} />;
  }
}

TabsItemView.propTypes = {
  overflow: PropTypes.oneOf(TabOverflowTypes),

  /** inherited props */
};

/**
 * Production ready Tabs and Items,
 * every Tab Group except last one are only required for cases
 * where one does not want to drag all the possible tab code into the project
 */
export const [SimpleTabsList, SimpleTabsItem] = withContext([
  SimpleTabsListView,
  SimpleTabsItemView,
]);
export const [DropdownTabsList, DropdownTabsItem] = withContext([
  DropdownTabsListView,
  DropdownTabsItemView,
]);
export const [ScrollableTabsList, ScrollableTabsItem] = withContext([
  ScrollableTabsListView,
  ScrollableTabsItemView,
]);

/**
 * Universal Tab View Group, given overflow and type arguments,
 * allows to change customize behavior and visuals on the fly
 */
export const [TabsList, TabsItem] = withContext([TabsListView, TabsItemView]);
export const [StatefulTabsList] = withState([TabsList]);
